/**
 * Lightweight default toast for a failed API call.
 *
 * Error handling for API calls is the caller's responsibility: a caller
 * `catch`es the rejection, deals with any error it wants to handle with
 * contextually appropriate feedback, and passes anything else here for a
 * generic toast. Takes the `Datastore` (it reads `AppData` off it) and accepts
 * `error: unknown` so callers don't repeat the type narrowing.
 *
 * The toast's text comes from the error's code, not its message, so it can be
 * translated (see `api-error-labels.ts`):
 *
 * - **ORPCError with a contract code** — that code's label, filled in from its
 *   `data` when the error is defined.
 * - **any other ORPCError** — a generic "something went wrong".
 * - **anything else** — a transport failure (fetch threw, body malformed), so
 *   a "couldn't reach the server" message under the `NETWORK` bucket.
 *
 * The server's own message is English and written for developers, so it goes
 * to the console instead of the toast. The dedupe id is keyed on the error code
 * so repeated failures of the same kind (e.g. a query the caller retries)
 * collapse into one visible toast (`addNotificationToast` ignores duplicate
 * ids).
 */
import { ORPCError } from '@orpc/client';
import { addNotificationToast } from 'component/notification-toast';
import { apiErrorLabels, fallbackApiErrorLabel, networkApiErrorLabel } from 'system/api-error-labels';
import type { Datastore } from 'util/datastore';
import type { FormatParams, TLabel } from 'util/stdtypes';

export function showApiErrorToast({ datastore, error }: { datastore: Datastore; error: unknown }) {
    const code = error instanceof ORPCError ? error.code : 'NETWORK';
    // eslint-disable-next-line no-console -- the server's message is for debugging, not the toast
    console.info(`API error ${code}:`, error instanceof Error ? error.message : String(error));
    const { label, formatParams } = apiErrorToastLabel(error);
    addNotificationToast({
        appData: datastore.getAppData(),
        id: `api_error_${code}`,
        type: 'error',
        duration: 7000,
        label,
        formatParams,
        dismissable: true,
    });
}

function apiErrorToastLabel(error: unknown): { label: TLabel; formatParams?: FormatParams } {
    if (!(error instanceof ORPCError)) {
        return { label: networkApiErrorLabel };
    }
    if (!Object.hasOwn(apiErrorLabels, error.code)) {
        return { label: fallbackApiErrorLabel };
    }
    const entry = apiErrorLabels[error.code as keyof typeof apiErrorLabels];
    // An undefined error carries no contract-checked `data`, so only a defined
    // one can fill in the label's values.
    const formatParams = error.defined && entry.params ? entry.params(error.data as never) : undefined;
    return { label: entry.label, formatParams };
}
